// src/padplanner/LandownerSection.jsx
//
// The plan page's Landowner section (S6b2), in the Review tab above the
// internal comments and apart from them: where the plan went, what the
// landowner said, and Submit to landowner. The rules are landowner.js's; the
// server (routes/padplannerLandowner.mjs) decides.
//
//   - One row per case over time: "Submitted to <owner> · <case> rev <n> ·
//     <status> · <k> findings", or "Not submitted to a landowner."
//   - A case returned with changes requested says so, and what comes next.
//   - Submit: the version on screen, to a landowner with a live relationship;
//     the plan's intended landowner (158) is chosen first when it can take
//     it, and the choice is still the box's.
//     Disabled with the reason (no relationship, revoked, the seat, the
//     version's status). Before anything is sent the confirm box shows the
//     file the landowner will get, read from the export the submission itself
//     uses (profile=landowner), and its SHA-256: what is listed is what goes.
//     Reading that file is recorded as an export, as any download is.
//   - Findings: the landowner's, read-only, numbered as in their portal;
//     picking one selects its pin and, on its own version, the feature.
import { useState } from "react";
import { SectionLabel } from "./SidePanel";
import ReadFailed from "./ReadFailed";
import { exportPlanGeoJson, submitToLandowner } from "../api/padplannerApi";
import { refusalText } from "./planRefusals";
import { formatUpdated } from "./planVocabulary";
import { findingNumber } from "../portal/portalFindingNumber";
import {
  NOT_SUBMITTED_TEXT, NOT_SENT_TEXT, caseRowText, changesRequested, submitOffer, whatGoes, whatGoesText,
} from "./landowner";

export default function LandownerSection({
  plan, revNo, revision, cases, casesError, relationships, relationshipsError, onRetry,
  inView, selectedId, onSelectFinding, onSubmitted,
}) {
  const back = cases ? changesRequested(cases, plan) : null;
  return (
    <section className="flex flex-col gap-2" aria-labelledby="pp-landowner-head" data-testid="pp-landowner">
      <SectionLabel id="pp-landowner-head">Landowner</SectionLabel>
      {casesError && <ReadFailed what="the landowner submissions" error={casesError} onRetry={onRetry} testid="pp-landowner-failed" />}
      {cases && cases.length === 0 && <p className="op-meta" data-testid="pp-landowner-none">{NOT_SUBMITTED_TEXT}</p>}
      {cases && cases.length > 0 && (
        <ul className="flex flex-col gap-1">
          {cases.map((c) => (
            <li key={c.plan_review_id} className="text-[13px] text-fg-primary" data-testid="pp-landowner-case" data-status={c.status ?? ""}>
              {caseRowText(c)}
            </li>
          ))}
        </ul>
      )}
      {back && (
        <div className="rounded border border-accent-action/40 bg-accent-action/10 px-2 py-1.5 text-[12.5px] text-fg-primary" role="status" data-testid="pp-landowner-changes">
          <p className="font-semibold">{back.text}</p>
          <p>{back.next}</p>
        </div>
      )}
      {relationshipsError
        ? <ReadFailed what="your landowner relationships" error={relationshipsError} onRetry={onRetry} testid="pp-landowner-rel-failed" />
        : cases && relationships && (
          <SubmitToLandowner key={revNo} plan={plan} revNo={revNo}
            offer={submitOffer({ relationships, revision, revNo, archived: Boolean(plan.archived_at), cases })}
            onSubmitted={onSubmitted} />
        )}
      {inView.findings.length > 0 && (
        <Findings inView={inView} selectedId={selectedId} onSelect={onSelectFinding} />
      )}
    </section>
  );
}

function SubmitToLandowner({ plan, revNo, offer, onSubmitted }) {
  const open = offer.owners.filter((o) => !o.reason);
  const intended = open.find((o) => o.id === plan.intended_landowner?.org_id);
  const [ownerId, setOwnerId] = useState(intended?.id ?? (open.length === 1 ? open[0].id : ""));
  // null | {busy} | {file: {n, sha256}} while confirming | {done}
  const [step, setStep] = useState(null);
  const [error, setError] = useState(null);
  const owner = offer.owners.find((o) => o.id === ownerId) ?? null;
  const disabledReason = offer.disabledReason ?? (owner?.reason || null);

  async function prepare() {
    setError(null);
    setStep({ busy: true });
    try {
      const f = await exportPlanGeoJson(plan.id, revNo, { profile: "landowner" });
      const n = whatGoes(JSON.parse(await f.blob.text()));
      setStep({ file: { n, sha256: f.sha256 } });
    } catch (err) { setStep(null); setError(refusalText(err)); }
  }

  async function send() {
    setError(null);
    setStep((s) => ({ ...s, busy: true }));
    try {
      const r = await submitToLandowner(plan.id, revNo, ownerId);
      const s = r.submission;
      setStep({ done: `Sent v${revNo} to ${s.owner_org_name}: ${s.case_number} rev ${s.plan_review_rev_no}${s.new_case ? " (a new case)" : ""}.` });
      onSubmitted?.(s);
    } catch (err) { setStep(null); setError(refusalText(err)); }
  }

  if (step?.done) return <p className="op-meta" role="status" data-testid="pp-landowner-sent">{step.done}</p>;

  return (
    <div className="flex flex-col gap-1.5" data-testid="pp-landowner-submit">
      {offer.owners.length > 1 && !offer.disabledReason && (
        <label className="flex items-center gap-2 text-[12.5px]">
          <span className="op-label uppercase">To</span>
          <select value={ownerId} onChange={(e) => { setOwnerId(e.target.value); setStep(null); }} data-testid="pp-landowner-owner"
            className="min-w-0 flex-1 rounded border border-bg-border bg-bg px-2 py-1 text-[13px] text-fg-primary">
            <option value="">Choose a landowner</option>
            {offer.owners.map((o) => <option key={o.id} value={o.id} disabled={Boolean(o.reason)}>{o.name}</option>)}
          </select>
        </label>
      )}
      {offer.owners.length === 1 && !offer.disabledReason && <p className="op-meta">To {offer.owners[0].name}</p>}
      {!step?.file && (
        <button type="button" className="op-btn op-btn--sm self-start" data-testid="pp-landowner-submit-btn"
          disabled={Boolean(disabledReason) || !ownerId || step?.busy} title={disabledReason ?? undefined} onClick={prepare}>
          Submit v{revNo} to landowner
        </button>
      )}
      {disabledReason && <p className="op-meta" data-testid="pp-landowner-disabled">{disabledReason}</p>}
      {step?.file && (
        <div className="flex flex-col gap-1.5 rounded border border-bg-border px-2 py-2 text-[12.5px]" role="dialog" aria-label="Confirm submission" data-testid="pp-landowner-confirm">
          <p className="text-fg-primary">Send v{revNo} of {plan.plan_number} to {owner?.name}: {whatGoesText(step.file.n)}.</p>
          <p className="op-meta">{NOT_SENT_TEXT}</p>
          <p className="op-meta break-all">SHA-256 <span className="op-mono" data-testid="pp-landowner-sha">{step.file.sha256}</span></p>
          <div className="flex gap-2">
            <button type="button" className="op-btn op-btn--primary op-btn--sm" disabled={step.busy} data-testid="pp-landowner-send" onClick={send}>Send</button>
            <button type="button" className="op-btn op-btn--sm" disabled={step.busy} onClick={() => setStep(null)}>Cancel</button>
          </div>
        </div>
      )}
      {error && <p className="text-sm text-accent-red" role="alert" data-testid="pp-landowner-error">{error}</p>}
    </div>
  );
}

function Findings({ inView, selectedId, onSelect }) {
  return (
    <div className="flex flex-col gap-1" data-testid="pp-landowner-findings">
      <SectionLabel>Landowner findings on v{inView.revNo} · {inView.findings.length}</SectionLabel>
      <p className="op-meta">The landowner's, read-only. Your internal comments are below.</p>
      <ul className="flex flex-col gap-1">
        {inView.findings.map((f) => (
          <li key={f.id}>
            <button type="button" aria-pressed={f.id === selectedId} data-testid="pp-landowner-finding"
              className={`flex w-full flex-col gap-0.5 rounded px-2 py-1.5 text-left text-[12.5px] hover:bg-bg-softer ${f.id === selectedId ? "bg-bg-softer" : ""}`}
              onClick={() => onSelect(f)}>
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="op-mono inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[4px] bg-[#7c3aed] px-1 text-[11px] font-bold text-white">
                  {findingNumber(f) ?? "•"}
                </span>
                <span className="op-chip">{f.markup_type}</span>
                <span className="op-meta">{f.feature_label ?? f.feature_tag}{f.case_number ? ` · ${f.case_number}` : ""}</span>
              </span>
              <span className="whitespace-pre-wrap text-fg-primary">{f.note}</span>
              <span className="op-meta">{f.author_username ?? "The landowner"} · {formatUpdated(f.frozen_at)}{f.geometry ? "" : " · no place of its own"}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
